import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { usePatientPicker } from '@/hooks/usePatientPicker';
import { useDecryptCpr } from '@/hooks/usePatients';
import {
  type SupForloebOption,
  SUP_TRANSACTION_LABELS,
  SUP_TRANSACTION_TYPES,
  type SupExportRow,
  type SupTransactionType,
  formatSupTime,
  hiddenSupFileName,
  supDefaultTransactionType,
  supForloebLabel,
  supRefusalMessage,
  unmaskSup,
} from '@/lib/sup/supExport';

// Spor A block 2 T2.6 (SUP-S 4.2): Standarder → SUP. Admin only.
// 'Dan SUP-udtræk' builds one SUP 3.0 file (communicate-sup → Communicate buildSup,
// recommended file name, XSD-validated) and logs it. 'Hent fil' downloads it.
// No transport: download or manual upload, never SFTP; there is no upload job.
// The file name and file are stored with the CPR as markers; the CPR is put back here
// only after an audited decrypt-cpr, and is kept in memory only.
// Medcom gap list 17.10.2026: one forløb per Clinio course (referral). Picking a patient
// loads its forløb; with an earlier export the type defaults to OpdaterSpecielt (2.7, the
// admin can change it). SletSpecielt deletes the one forløb chosen here (2.16).

const COLUMNS = 'id, created_at, patient_id, transaction_type, priority, persons_count, extract_id, extract_id_is_test, file_name_masked, size_bytes, xsd_ok, xsd_errors';

export function SupExportPanel({ clinicId, canExport }: { clinicId: string; canExport: boolean }) {
  const { t } = useTranslation();
  const decrypt = useDecryptCpr();
  const [search, setSearch] = useState('');
  const [patientId, setPatientId] = useState<string | null>(null);
  const [patientLabel, setPatientLabel] = useState('');
  const [transactionType, setTransactionType] = useState<SupTransactionType>('Opdater');
  const [forloeb, setForloeb] = useState<SupForloebOption[] | null>(null);
  const [earlierExports, setEarlierExports] = useState(0);
  const [forloebId, setForloebId] = useState('');
  const [forloebError, setForloebError] = useState<string | null>(null);
  const forloebSeq = useRef(0);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [rows, setRows] = useState<SupExportRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [cprs, setCprs] = useState<Record<string, string>>({});
  const [names, setNames] = useState<Record<string, string>>({});
  const seq = useRef(0);
  const { data: patients } = usePatientPicker(canExport ? clinicId : null, search);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoadError(null);
    const { data, error } = await supabase
      .from('clinic_sup_exports')
      .select(COLUMNS)
      .eq('clinic_id', clinicId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (mine !== seq.current) return;
    if (error) {
      setLoadError(t('medcom.sup.load_failed', 'SUP-udtrækkene kunne ikke hentes.'));
      return;
    }
    setRows((data ?? []) as unknown as SupExportRow[]);
  }, [clinicId, t]);

  useEffect(() => {
    setRows(null);
    setCprs({});
    setNames({});
    if (canExport) void load();
  }, [canExport, clinicId, load]);

  const cprFor = useCallback(
    async (pid: string, source: string): Promise<string> => {
      if (cprs[pid]) return cprs[pid];
      const result = await decrypt.mutateAsync({ patient_id: pid, source, purpose: 'SUP-udtræk' });
      setCprs((prev) => ({ ...prev, [pid]: result.cpr }));
      return result.cpr;
    },
    // mutateAsync is stable; the mutation object is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cprs],
  );

  const loadForloeb = useCallback(
    async (pid: string, setDefault: boolean) => {
      const mine = ++forloebSeq.current;
      setForloebError(null);
      try {
        const data = await callFunction<Record<string, unknown>, { forloeb: SupForloebOption[]; earlier_exports: number }>('communicate-sup', {
          action: 'forloeb',
          clinic_id: clinicId,
          patient_id: pid,
        });
        if (mine !== forloebSeq.current) return;
        setForloeb(data?.forloeb ?? []);
        setEarlierExports(data?.earlier_exports ?? 0);
        if (setDefault) setTransactionType(supDefaultTransactionType(data?.earlier_exports ?? 0));
      } catch {
        if (mine !== forloebSeq.current) return;
        setForloeb(null);
        setForloebError(t('medcom.sup.forloeb_failed', 'Patientens forløb kunne ikke hentes.'));
      }
    },
    [clinicId, t],
  );

  const needsForloeb = transactionType === 'SletSpecielt';
  const canChooseForloeb = transactionType === 'SletSpecielt' || transactionType === 'OpdaterSpecielt';
  const blockedReason = !patientId
    ? null
    : needsForloeb && !forloebId
      ? t('medcom.sup.choose_forloeb_required', 'Vælg det forløb, der skal slettes. SletSpecielt sletter kun ét forløb.')
      : null;

  async function build() {
    if (!patientId) return;
    if (blockedReason) {
      setRefusal(blockedReason);
      return;
    }
    setBusy(true);
    setRefusal(null);
    try {
      await callFunction('communicate-sup', {
        action: 'export',
        clinic_id: clinicId,
        patient_id: patientId,
        transaction_type: transactionType,
        ...(canChooseForloeb && forloebId ? { forloeb_id: forloebId } : {}),
      });
      await load();
      void loadForloeb(patientId, false);
    } catch (err) {
      setRefusal(supRefusalMessage(err instanceof ApiCallError ? err : null));
    } finally {
      setBusy(false);
    }
  }

  async function showName(row: SupExportRow) {
    setRowError(null);
    try {
      const cpr = await cprFor(row.patient_id, 'sup-export-view');
      const name = unmaskSup(row.file_name_masked, cpr);
      if (!name) throw new Error('cpr');
      setNames((prev) => ({ ...prev, [row.id]: name }));
    } catch {
      setRowError(t('medcom.sup.reveal_failed', 'Filnavnet kunne ikke vises (CPR kunne ikke læses).'));
    }
  }

  async function download(row: SupExportRow) {
    setRowError(null);
    try {
      const { data, error } = await supabase.from('clinic_sup_exports').select('xml_masked').eq('id', row.id).single();
      if (error || !data) throw new Error('read');
      const cpr = await cprFor(row.patient_id, 'sup-export-download');
      const name = unmaskSup(row.file_name_masked, cpr);
      const xml = unmaskSup((data as { xml_masked: string }).xml_masked, cpr);
      if (!name || !xml) throw new Error('cpr');
      setNames((prev) => ({ ...prev, [row.id]: name }));
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setRowError(t('medcom.sup.download_failed', 'Filen kunne ikke hentes.'));
    }
  }

  if (!canExport) {
    return (
      <p className="text-sm text-ds-muted" data-testid="sup-admin-only">
        {t('medcom.sup.admin_only', 'SUP-udtræk kan kun dannes og hentes af administratorer.')}
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="sup-export-panel">
      <div className="rounded-md border border-ds-teal-line bg-ds-teal-tint p-3 text-sm text-ds-teal" data-testid="sup-no-sftp">
        {t(
          'medcom.sup.delivery_note',
          "Filen leveres ved download ('Hent fil') eller manuel upload. Clinio sender ikke via SFTP: der er ingen forbindelse til E-journal og intet uploadjob.",
        )}
      </div>

      <div className="space-y-3 rounded-md border border-ds-line p-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('medcom.sup.patient', 'Patient')}</span>
          <input
            className="w-full rounded-md border border-ds-field px-3 py-1.5 text-sm"
            placeholder={t('medcom.sup.patient_search', 'Søg patient')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        {patientId ? (
          <p className="text-sm">
            {t('medcom.sup.selected', 'Valgt')}: <span className="font-medium">{patientLabel}</span>
          </p>
        ) : null}
        {search.trim() ? (
          <ul className="max-h-40 space-y-1 overflow-auto text-sm">
            {(patients ?? []).map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={`w-full rounded px-2 py-1 text-left hover:bg-ds-row-hover  ${p.id === patientId ? 'bg-ds-neutral-tint font-medium ' : ''}`}
                  onClick={() => {
                    setPatientId(p.id);
                    setPatientLabel(`${p.first_name} ${p.last_name}`);
                    setRefusal(null);
                    setForloeb(null);
                    setForloebId('');
                    setEarlierExports(0);
                    void loadForloeb(p.id, true);
                  }}
                >
                  {`${p.first_name} ${p.last_name}`}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('medcom.sup.transaction_type', 'Transaktionstype')}</span>
          <select
            className="w-full rounded-md border border-ds-field px-3 py-2 text-sm"
            value={transactionType}
            onChange={(e) => {
              setTransactionType(e.target.value as SupTransactionType);
              setRefusal(null);
            }}
            data-testid="sup-transaction-type"
          >
            {SUP_TRANSACTION_TYPES.map((tt) => (
              <option key={tt} value={tt}>
                {SUP_TRANSACTION_LABELS[tt]}
              </option>
            ))}
          </select>
        </label>
        {patientId && earlierExports > 0 ? (
          <p className="text-sm text-ds-text" data-testid="sup-earlier-exports">
            {t('medcom.sup.earlier_exports', {
              defaultValue: 'Patienten har {{count}} tidligere SUP-udtræk. Transaktionstypen er derfor sat til OpdaterSpecielt; du kan ændre den.',
              count: earlierExports,
            })}
          </p>
        ) : null}
        {patientId && forloeb ? (
          <p className="text-sm text-ds-text" data-testid="sup-forloeb-count">
            {forloeb.some((f) => f.kind === 'course')
              ? t('medcom.sup.forloeb_count', {
                  defaultValue: 'Forløb i udtrækket: {{count}} (ét pr. henvisning).',
                  count: forloeb.filter((f) => f.kind === 'course').length,
                })
              : t('medcom.sup.no_forloeb', 'Patienten har ingen forløb (henvisninger) i Clinio. Der kan kun dannes Slet, eller SletSpecielt af et tidligere sendt forløb.')}
          </p>
        ) : null}
        {forloebError ? (
          <p role="alert" className="text-sm text-ds-red-ink" data-testid="sup-forloeb-error">
            {forloebError}
          </p>
        ) : null}
        {patientId && canChooseForloeb ? (
          <label className="block text-sm">
            <span className="mb-1 block font-medium">
              {needsForloeb ? t('medcom.sup.forloeb_delete', 'Forløb der skal slettes') : t('medcom.sup.forloeb_update', 'Forløb')}
            </span>
            <select
              className="w-full rounded-md border border-ds-field px-3 py-2 text-sm"
              value={forloebId}
              onChange={(e) => {
                setForloebId(e.target.value);
                setRefusal(null);
              }}
              data-testid="sup-forloeb"
            >
              <option value="">{needsForloeb ? t('medcom.sup.forloeb_choose', 'Vælg forløb') : t('medcom.sup.forloeb_all', 'Alle forløb')}</option>
              {(forloeb ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {supForloebLabel(f)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {blockedReason ? (
          <p className="text-sm text-ds-amber-ink" data-testid="sup-blocked-reason">
            {blockedReason}
          </p>
        ) : null}
        <Button type="button" disabled={!patientId || busy} onClick={() => void build()} data-testid="sup-build">
          {busy ? t('medcom.sup.building', 'Danner…') : t('medcom.sup.build', 'Dan SUP-udtræk')}
        </Button>
        {refusal ? (
          <p role="alert" className="rounded-md border border-ds-red bg-ds-red-tint p-2 text-sm text-ds-red-ink" data-testid="sup-refusal">
            {refusal}
          </p>
        ) : null}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">{t('medcom.sup.log_title', 'SUP-udtræk')}</h3>
        {loadError ? <p role="alert" className="text-sm text-ds-red-ink">{loadError}</p> : null}
        {rowError ? <p role="alert" className="text-sm text-ds-red-ink">{rowError}</p> : null}
        {rows && rows.length === 0 ? <p className="text-sm text-ds-muted">{t('medcom.sup.empty', 'Der er ikke dannet nogen SUP-udtræk endnu.')}</p> : null}
        {rows && rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm" data-testid="sup-log">
              <thead className="text-xs uppercase text-ds-muted">
                <tr>
                  <th className="px-2 py-1">{t('medcom.sup.col_time', 'Tidspunkt')}</th>
                  <th className="px-2 py-1">{t('medcom.sup.col_file', 'Filnavn')}</th>
                  <th className="px-2 py-1">{t('medcom.sup.col_persons', 'Personer')}</th>
                  <th className="px-2 py-1">{t('medcom.sup.col_type', 'Transaktionstype')}</th>
                  <th className="px-2 py-1">{t('medcom.sup.col_xsd', 'XSD')}</th>
                  <th className="px-2 py-1" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-ds-line align-top">
                    <td className="whitespace-nowrap px-2 py-1">{formatSupTime(row.created_at)}</td>
                    <td className="px-2 py-1 font-mono text-xs">
                      <span className="break-all">{names[row.id] ?? hiddenSupFileName(row.file_name_masked)}</span>
                      {names[row.id] ? null : (
                        <button type="button" className="ml-2 text-xs text-ds-teal underline" onClick={() => void showName(row)}>
                          {t('medcom.sup.show_name', 'Vis filnavn')}
                        </button>
                      )}
                      {row.extract_id_is_test ? (
                        <span className="ml-2 rounded bg-ds-amber-tint px-1 text-[10px] text-ds-amber-ink">{t('medcom.sup.test_id', 'test-udtræks-id')}</span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1">{row.persons_count}</td>
                    <td className="px-2 py-1">{row.transaction_type}</td>
                    <td className="px-2 py-1">
                      {row.xsd_ok ? (
                        <span className="text-ds-green-ink">{t('medcom.sup.xsd_ok', 'Gyldig')}</span>
                      ) : (
                        <details>
                          <summary className="cursor-pointer text-ds-red-ink">
                            {t('medcom.sup.xsd_failed', 'Ugyldig')} ({row.xsd_errors.length})
                          </summary>
                          <ul className="mt-1 list-disc pl-4 text-xs">
                            {row.xsd_errors.map((e, i) => (
                              <li key={i}>{e}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <Button type="button" size="sm" variant="secondary" onClick={() => void download(row)}>
                        {t('medcom.sup.download', 'Hent fil')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
